import { OrderStatus, PersistedOrderPayload, ThemeColors, MenuItem, FontConfig, ZoneColors } from '@onesunday/domain';
// ColorRole no longer imported — ZoneTheme now stores hex strings directly

const ADMIN_API_KEY_STORAGE_KEY = 'onesunday.adminApiKey';
const DEFAULT_ADMIN_API_KEY = import.meta.env.VITE_ADMIN_API_KEY || import.meta.env.ADMIN_API_KEY || '';

const readStoredAdminApiKey = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.localStorage.getItem(ADMIN_API_KEY_STORAGE_KEY) || '';
};

const getActiveAdminApiKey = () => {
  const stored = readStoredAdminApiKey();
  return (stored || DEFAULT_ADMIN_API_KEY).trim();
};

const readErrorMessage = async (res: Response, fallback: string) => {
  const text = await res.text();
  if (!text) {
    return fallback;
  }

  try {
    const parsed = JSON.parse(text) as { error?: string; message?: string };
    if (typeof parsed?.error === 'string' && parsed.error.trim()) {
      return parsed.error.trim();
    }
    if (typeof parsed?.message === 'string' && parsed.message.trim()) {
      return parsed.message.trim();
    }
  } catch {
    // Fall back to the raw response body when it is not JSON.
  }

  return text;
};

const buildHeaders = () => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const adminApiKey = getActiveAdminApiKey();
  if (adminApiKey) {
    headers['x-api-key'] = adminApiKey;
  }
  return headers;
};

export const getAdminApiKey = () => getActiveAdminApiKey();

export const setAdminApiKey = (adminApiKey: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  const normalized = adminApiKey.trim();
  if (normalized) {
    window.localStorage.setItem(ADMIN_API_KEY_STORAGE_KEY, normalized);
    return;
  }

  window.localStorage.removeItem(ADMIN_API_KEY_STORAGE_KEY);
};

export const clearAdminApiKey = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.removeItem(ADMIN_API_KEY_STORAGE_KEY);
};

export interface ListOrdersParams {
  page?: number;
  pageSize?: number;
  status?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
  source?: string;
}

export interface ListOrdersResponse {
  page: number;
  pageSize: number;
  total: number;
  results: PersistedOrderPayload[];
}

export const fetchOrders = async (params: ListOrdersParams = {}): Promise<ListOrdersResponse> => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  const res = await fetch(`/api/listOrders?${searchParams.toString()}`, {
    method: 'GET',
    headers: buildHeaders(),
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, `Failed to fetch orders (${res.status})`));
  }
  return res.json();
};

export interface PersistedEventLite {
  id: string;
  eventType: string;
  sessionId: string | null;
  orderId: string | null;
  timestamp: string | null;
  utmSource: string | null;
  referrer: string | null;
  userAgent: string | null;
  timezone: string | null;
  language: string | null;
  geoCountry: string | null;
  geoCountryCode: string | null;
  geoRegion: string | null;
  geoCity: string | null;
  pathname: string | null;
  connectionEffectiveType: string | null;
  connectionDownlink: number | null;
}

export interface ListEventsParams {
  startDate?: string;
  endDate?: string;
  pageSize?: number;
}

export interface ListEventsResponse {
  pageSize: number;
  total: number;
  truncated?: boolean;
  results: PersistedEventLite[];
}

export const fetchEvents = async (params: ListEventsParams = {}): Promise<ListEventsResponse> => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  const res = await fetch(`/api/listEvents?${searchParams.toString()}`, {
    method: 'GET',
    headers: buildHeaders(),
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, `Failed to fetch events (${res.status})`));
  }
  return res.json();
};

export interface AnalyticsInsightsResponse {
  generatedAt: string;
  schemaVersion: number;
  scope: { campaignId: string; startDate: string | null; endDate: string | null };
  metrics: { events: number; trackedBrowsers: number; sessions: number; engagedSessions: number; checkoutSessions: number; paymentSessions: number; purchasingSessions: number; confirmedOrders: number; revenue: number };
  coverage: { campaignContext: number | null; attribution: number | null; geo: number | null; matchedPurchases: number | null };
  breakdowns: { acquisition: Array<{ name: string; value: number }>; devices: Array<{ name: string; value: number }>; operatingSystems: Array<{ name: string; value: number }>; countries: Array<{ name: string; value: number }>; cities: Array<{ name: string; value: number }>; eventTypes: Array<{ name: string; value: number }> };
  notes: string[];
}

export const fetchAnalyticsInsights = async (params: { campaignId?: string; startDate?: string; endDate?: string } = {}): Promise<AnalyticsInsightsResponse> => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => { if (value) searchParams.set(key, value); });
  const res = await fetch(`/api/analyticsInsights?${searchParams}`, { method: 'GET', headers: buildHeaders() });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Failed to fetch analytics insights (${res.status})`));
  return res.json();
};

export interface SendCustomerEmailPayload {
  subject: string;
  bodyText: string;
  eventDate?: string;
  createdFrom?: string;
  createdTo?: string;
  source?: string;
  dryRun?: boolean;
}

export interface SendCustomerEmailDryRun {
  dryRun: true;
  matched: number;
  eligible: number;
  pickup: { address: string; hours: string; contact: string };
  brandName: string;
  samplePreview: { subject: string; text: string } | null;
  sample: Array<{ orderId: string; name: string; email: string }>;
}

export interface SendCustomerEmailResult {
  dryRun?: false;
  matched: number;
  eligible: number;
  sent: number;
  failed: number;
  skipped: number;
}

export type SendCustomerEmailResponse = SendCustomerEmailDryRun | SendCustomerEmailResult;

export const sendCustomerEmail = async (
  payload: SendCustomerEmailPayload,
): Promise<SendCustomerEmailResponse> => {
  const res = await fetch('/api/sendCustomerEmail', {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, `Failed to send (${res.status})`));
  }
  return res.json();
};

export interface UpdateOrderStatusPayload {
  orderId: string;
  status: OrderStatus;
  paymentInfo?: PersistedOrderPayload['paymentInfo'];
  paymentId?: string;
}

export const updateOrderStatus = async (payload: UpdateOrderStatusPayload): Promise<PersistedOrderPayload> => {
  const res = await fetch('/api/updateOrderStatus', {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, `Failed to update order (${res.status})`));
  }
  return res.json();
};

export interface YocoAmount {
  amount: number;
  currency: string;
}

export interface YocoPayment {
  id: string;
  order_id?: string;
  display_name?: string;
  receipt_number?: string;
  created_at?: string;
  updated_at?: string;
  status?: string;
  payment_method?: string;
  payment_source?: string;
  total_amount?: YocoAmount;
  amount_excl_tip?: YocoAmount;
  tip_amount?: YocoAmount;
  refunded_amount?: YocoAmount;
}

export interface ListPaymentsResponse {
  data: YocoPayment[];
  next_cursor?: string;
}

export interface FetchPaymentResponse extends YocoPayment {}

export const fetchPaymentById = async (paymentId: string, params: { mode?: 'live' | 'test' } = {}): Promise<FetchPaymentResponse> => {
  if (!paymentId) {
    throw new Error('paymentId is required');
  }
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  const url = `/api/listPayments/${encodeURIComponent(paymentId)}?${searchParams.toString()}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: buildHeaders(),
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, `Failed to fetch payment (${res.status})`));
  }
  return res.json();
};

export interface ListPaymentsParams {
  limit?: number;
  from?: string;
  to?: string;
  daysBack?: number;
  cursor?: string;
  mode?: 'live' | 'test';
}

export const fetchPayments = async (params: ListPaymentsParams = {}): Promise<ListPaymentsResponse> => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  const url = `/api/listPayments?${searchParams.toString()}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: buildHeaders(),
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, `Failed to fetch payments (${res.status})`));
  }
  return res.json();
};

export interface PaymentReconRow {
  orderId: string;
  status: string;
  checkoutId?: string | null;
  orderAmountCents?: number | null;
  yocoAmountCents?: number | null;
  diffCents?: number | null;
  sourceFile?: string | null;
  customerName?: string | null;
  items?: { name?: string; quantity?: number }[];
  paymentId?: string | null;
  yocoStatus?: string | null;
}

export interface PaymentReconResponse {
  summary: {
    orders: number;
    matched: number;
    withCsv: number;
    mismatched: number;
    csvRows: number;
  };
  rows: PaymentReconRow[];
}

export interface PaymentReconParams {
  daysBack?: number;
  from?: string;
  to?: string;
}

export const fetchPaymentRecon = async (params: PaymentReconParams = {}): Promise<PaymentReconResponse> => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  const url = `/api/reconPayments?${searchParams.toString()}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: buildHeaders(),
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, `Failed to fetch payment recon (${res.status})`));
  }
  return res.json();
};

export interface AppSettingsResponse {
  paymentsEnabled: boolean;
  pickupCampaignEnabled: boolean;
  pickupAddress: string;
  pickupHours: string;
  pickupContact: string;
  eventDate: string;
  flyerUrl: string;
  themeColors: ThemeColors;
  menuItems: MenuItem[];
  fontConfig: FontConfig;
  zoneColors: ZoneColors;
  extendedColors: string[];
  updatedAt?: string;
}

export interface UpdateAppSettingsPayload {
  paymentsEnabled?: boolean;
  pickupCampaignEnabled?: boolean;
  pickupAddress?: string;
  pickupHours?: string;
  pickupContact?: string;
  eventDate?: string;
  flyerUrl?: string;
  themeColors?: Partial<ThemeColors>;
  menuItems?: MenuItem[];
  fontConfig?: Partial<FontConfig>;
  zoneColors?: Partial<ZoneColors>;
  extendedColors?: string[];
}

export const fetchAppSettings = async (campaignId?: string): Promise<AppSettingsResponse> => {
  const url = campaignId
    ? `/api/getAppSettings?campaignId=${encodeURIComponent(campaignId)}`
    : '/api/getAppSettings';
  const res = await fetch(url, {
    method: 'GET',
    headers: buildHeaders(),
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, `Failed to fetch app settings (${res.status})`));
  }
  return res.json();
};

export const updateAppSettings = async (
  payload: UpdateAppSettingsPayload,
  campaignId?: string
): Promise<AppSettingsResponse> => {
  const url = campaignId
    ? `/api/updateAppSettings?campaignId=${encodeURIComponent(campaignId)}`
    : '/api/updateAppSettings';
  const res = await fetch(url, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, `Failed to update app settings (${res.status})`));
  }
  return res.json();
};

/** Fields that can be extracted from a flyer image via OCR. */
export interface FlyerExtracted {
  address: string | null;
  venueName: string | null;
  eventHours: string | null;
  eventDate: string | null;
  contactNumber: string | null;
}

export const uploadFlyer = async (base64Data: string, mimeType: string): Promise<string> => {
  const res = await fetch('/api/uploadFlyer', {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify({ data: base64Data, mimeType }),
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, `Upload failed (${res.status})`));
  }
  const json = await res.json() as { url: string };
  return json.url;
};
